import { useEffect, useRef, useState } from 'react';
import type { ReaderTheme } from '@/pages/reader/types';

interface SelectionState {
  text: string;
  rect: DOMRect;
}

interface Props {
  selection: SelectionState;
  onSave: (data: { note: string; isPublic: boolean }) => void;
  onCancel: () => void;
  theme?: ReaderTheme;
}

function composeColors(theme: ReaderTheme) {
  if (theme === 'dark') return {
    card: '#252525',
    cardBorder: '1px solid #3a3a3a',
    excerptBg: '#2d2a1e',
    excerptBorder: '#6b5a2a',
    excerptText: '#d4b88a',
    quoteIcon: '#c0943a',
    textareaBg: '#1e1e1e',
    textareaBorder: '#3a3a3a',
    textareaText: '#e8e8e8',
    toggleBorder: '#3a3a3a',
    toggleInactiveText: '#9ca3af',
    cancelBg: '#2a2a2a',
    cancelBorder: '#3a3a3a',
    cancelText: '#d1d5db',
  };
  if (theme === 'sepia') return {
    card: '#f0ebe0',
    cardBorder: '1px solid #d6cdb8',
    excerptBg: '#faf5e4',
    excerptBorder: '#d4b88a',
    excerptText: '#5a4020',
    quoteIcon: '#b07830',
    textareaBg: '#faf5e4',
    textareaBorder: '#d6cdb8',
    textareaText: '#3a2e1a',
    toggleBorder: '#d6cdb8',
    toggleInactiveText: '#7a6a4a',
    cancelBg: '#e8e0d0',
    cancelBorder: '#d6cdb8',
    cancelText: '#5a4020',
  };
  return {
    card: '#ffffff',
    cardBorder: 'none',
    excerptBg: '#fefce8',
    excerptBorder: '#fde047',
    excerptText: '#374151',
    quoteIcon: '#d97706',
    textareaBg: '#ffffff',
    textareaBorder: '#e5e7eb',
    textareaText: '#111827',
    toggleBorder: '#e5e7eb',
    toggleInactiveText: '#6b7280',
    cancelBg: '#ffffff',
    cancelBorder: '#e5e7eb',
    cancelText: '#374151',
  };
}

export function AnnotationCompose({ selection, onSave, onCancel, theme = 'light' }: Props) {
  const [note, setNote] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const ref = useRef<HTMLTextAreaElement>(null);
  const c = composeColors(theme);

  useEffect(() => { ref.current?.focus(); }, []);

  const excerpt = selection.text.slice(0, 140) + (selection.text.length > 140 ? '…' : '');

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onCancel}
    >
      <div
        className="flex w-full max-w-lg flex-col gap-2.5 rounded-t-2xl p-5 shadow-2xl sm:rounded-2xl"
        style={{ background: c.card, border: c.cardBorder }}
        onClick={e => e.stopPropagation()}
      >
        {/* Excerpt preview */}
        <div
          className="flex items-start gap-2 rounded-lg px-3 py-2.5"
          style={{ background: c.excerptBg, border: `1px solid ${c.excerptBorder}` }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={c.quoteIcon} strokeWidth="2.5" className="mt-0.5 shrink-0">
            <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
            <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
          </svg>
          <span className="text-sm italic leading-snug" style={{ color: c.excerptText }}>{excerpt}</span>
        </div>

        <textarea
          ref={ref}
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Add your annotation…"
          rows={4}
          className="w-full resize-y rounded-lg px-3 py-2.5 text-sm leading-relaxed outline-none transition-colors focus:ring-1 focus:ring-[#1a56a0]"
          style={{
            background: c.textareaBg,
            border: `1px solid ${c.textareaBorder}`,
            color: c.textareaText,
          }}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div
            className="flex overflow-hidden rounded-lg"
            style={{ border: `1px solid ${c.toggleBorder}` }}
          >
            <button
              onClick={() => setIsPublic(true)}
              className="px-3 py-1.5 text-sm transition-colors"
              style={isPublic ? { background: '#2f3e46', color: '#ffffff' } : { background: c.card, color: c.toggleInactiveText }}
            >
              👁 Public
            </button>
            <button
              onClick={() => setIsPublic(false)}
              className="px-3 py-1.5 text-sm transition-colors"
              style={!isPublic ? { background: '#2f3e46', color: '#ffffff' } : { background: c.card, color: c.toggleInactiveText }}
            >
              🔒 Private
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors"
              style={{ background: c.cancelBg, border: `1px solid ${c.cancelBorder}`, color: c.cancelText }}
            >
              Cancel
            </button>
            <button
              onClick={() => note.trim() && onSave({ note, isPublic })}
              className={`rounded-md bg-[#2f3e46] px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33] ${!note.trim() ? 'opacity-50' : ''}`}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
